import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Paper, TextField, Button, FormControl, InputLabel, Select,
  MenuItem, FormControlLabel, Radio, RadioGroup, Typography, Box,
  CircularProgress, Avatar, Divider
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import api from '../services/api';

const MarksheetForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [levels, setLevels] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [templates, setTemplates] = useState([]);

  const [formData, setFormData] = useState({
    level: '',
    level_name: '',
    specialization: '',
    specialization_name: '',
    template: '',
    student_name: '',
    student_reg_no: '',
    final_result: 'Pass',
  });

  useEffect(() => {
    const fetchLevels = async () => {
      try {
        const response = await api.get('/api/levels/');
        setLevels(response.data);
      } catch (error) {
        console.error('Failed to fetch levels:', error);
      }
    };
    fetchLevels();
  }, []);

  const handleInputChange = async (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'level' && { specialization: '', template: '' }),
      ...(name === 'specialization' && { template: '' }),
    }));

    if (name === 'level') {
      try {
        const selectedLevel = levels.find(lvl => lvl._id === value);
        const res = await api.get(`/api/specializations/?level_id=${value}`);
        setSpecializations(res.data);
        setTemplates([]);
        setFormData(prev => ({
          ...prev,
          level: selectedLevel?._id || '',
          level_name: selectedLevel?.name || '',
          specialization: '',
          specialization_name: '',
          template: '',
        }));
        return;
      } catch (err) {
        console.error('Error fetching specializations:', err);
      }
    }

    if (name === 'specialization') {
      const selected = specializations.find(spec => spec._id === value);
      if (selected) {
        setTemplates(selected.templates || []);
        setFormData(prev => ({
          ...prev,
          specialization: selected._id,
          specialization_name: selected.name,
          template: '', // reset template
        }));
        return;
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/api/marksheetgen/', formData, {
        responseType: 'json',
      });
      if (response.data && response.data.marksheet_url) {
        // Redirect to results page after successful submission
        navigate('/results');
      } else {
        alert('Marksheet generated, but no file URL returned.');
      }
    } catch (error) {
      alert('Error generating marksheet.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', py: 6 }}>
      <Container maxWidth="sm">
        <Paper elevation={6} sx={{ p: 4, borderRadius: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
            <Avatar sx={{ bgcolor: '#00695f', width: 64, height: 64, mb: 1 }}>
              <SchoolIcon sx={{ fontSize: 40 }} />
            </Avatar>
            <Typography variant="h4" fontWeight={700} color="#00695f" gutterBottom align="center">
              Marksheet Generator
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" align="center" sx={{ mb: 2 }}>
              Generate professional marksheets in seconds for your students.
            </Typography>
          </Box>
          <Divider sx={{ mb: 3 }} />
          <form onSubmit={handleSubmit}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Level</InputLabel>
              <Select
                name="level"
                value={formData.level}
                onChange={handleInputChange}
                required
              >
                {levels.map(level => (
                  <MenuItem key={level._id} value={level._id}>{level.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>Specialization</InputLabel>
              <Select
                name="specialization"
                value={formData.specialization}
                onChange={handleInputChange}
                required
                disabled={!formData.level}
              >
                {specializations.map(spec => (
                  <MenuItem key={spec._id} value={spec._id}>{spec.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>Template</InputLabel>
              <Select
                name="template"
                value={formData.template}
                onChange={handleInputChange}
                required
                disabled={!formData.specialization}
              >
                {templates.map(template => (
                  <MenuItem key={template.filename} value={template.filename}>{template.filename}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              margin="normal"
              label="Student Name"
              name="student_name"
              value={formData.student_name}
              onChange={handleInputChange}
              required
            />

            <TextField
              fullWidth
              margin="normal"
              label="Student Registration Number"
              name="student_reg_no"
              value={formData.student_reg_no}
              onChange={handleInputChange}
              required
            />

            <FormControl margin="normal" sx={{ mt: 2 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                Final Result
              </Typography>
              <RadioGroup
                name="final_result"
                value={formData.final_result}
                onChange={handleInputChange}
                row
              >
                <FormControlLabel
                  value="Pass"
                  control={<Radio sx={{ color: '#00695f', '&.Mui-checked': { color: '#00695f' } }} />}
                  label="Pass"
                />
                <FormControlLabel
                  value="Refer"
                  control={<Radio sx={{ color: '#b71c1c', '&.Mui-checked': { color: '#b71c1c' } }} />}
                  label="Refer"
                />
              </RadioGroup>
            </FormControl>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={loading}
              sx={{ mt: 3, bgcolor: '#00695f', fontWeight: 600, letterSpacing: 1, '&:hover': { bgcolor: '#004d40' } }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'GENERATE MARKSHEET'}
            </Button>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default MarksheetForm; 